import { useForm, Controller } from 'react-hook-form'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import MaskedInput from 'react-text-mask'
import api from 'helpers/api'
import Spinner from 'components/common/Spinner/Spinner'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import Button from 'components/common/Button/Button'
import Input from 'components/common/Input/Input'
import InputError from 'components/common/InputError/InputError'
import Title from 'components/common/PageTitle/PageTitle'
import { expirationDateValidationFx, numberValidationCardNumberFx, numberValidationCVCFx, formParams } from 'constants/inputValidation'
import { next } from 'components/Freemium/Nav/Nav'
import mainStyle from '../../FreemiumComponents.module.scss'

const CardForm = ({ state }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [errorMessage, setErrorMessage] = useState(false);
  const [isSpinnerLoading, setIsSpinnerLoading] = useState(false)


  // Initialize form
  const required = t('common:form.incorrect')
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    control
  } = useForm({
    ...formParams,
    values:{
      owner: '',
      number: '',
      expirationDate: '',
      ccv: ''
    }
  })


  // Submit form
  const onSubmit = handleSubmit(async data => {
    try {
      setIsSpinnerLoading(true);
      const [expMonth, expYear] = data.expirationDate.split('/')
      const { paymentPlan, contractType, calculatedPrice } = state.contract
      const {
        genderPartner,
        firstnamePartner,
        lastnamePartner,
        birthdatePartner,
        placeOfBirthPartner,
        countryOfBirthPartner
      } = state.client

      const saveID = await Promise.all(contractType.map(async (contract, contractIndex) => {
        const isMium = contractIndex === contractType.length - 1
        const endFree = new Date(state.endFree)
        const res = await api.post({
          url: '/adhesions/client',
          data: {
            ...state.listingData,
            ...state.contract,
            ...state.client,
            calculatedPrice: isMium
              ? Number(calculatedPrice.replace(',','.'))
              : 0,
            idCampaign: state.idCampaign,
            phone: state.client.phone.number,
            countryCode: state.client.phone.countryCode,
            transactionHorodate: new Date(),
            paymentType: isMium 
              ? 'credit card'
              : 'Direct',
            paymentPlan,
            contractType: contract._id,
            contractPeriodType: contract.contractPeriodType[0]._id,
            priceType: contract.priceType[0]._id,
            rateType: contract.priceType[0].rateType[0]._id,
            cardOwnerFullName: data.owner,
            cardNumber: data.number.replace(/\D/g, ''),
            cvc: data.ccv.replace(/\D/g, ''),
            cardExpMonth: expMonth,
            cardExpYear: expYear,
            frequency: paymentPlan,
            dependent: state.client.partnerOption && {
              gender: genderPartner,
              firstname: firstnamePartner,
              lastname: lastnamePartner,
              birthdate: birthdatePartner,
              placeOfBirth: placeOfBirthPartner,
              countryOfBirth: countryOfBirthPartner
            },
            children: isMium 
              ? state.client.children
              : undefined,
            firstPaymentDate: !state.endFree && isMium
              // Free didn't start yet => mium start in the 1st in 4 months
              ? new Date(
                new Date().getFullYear(),
                new Date().getMonth() + 4,
                1
              )
              // Free already started
              : (endFree > new Date() && isMium
                // Free still active. Mium starts right after the free ends
                ? new Date(
                  endFree.getFullYear(),
                  endFree.getMonth(),
                  endFree.getDate() + 1
                )
                // Free over. Mium starts the 1st next month
                : new Date(
                  new Date().getFullYear(),
                  new Date().getMonth() + 1,
                  1
                )
              ),
            cancelationHorodate: !isMium
              // free => last day in 3 months
              ? new Date(
                new Date().getFullYear(),
                new Date().getMonth() + 4,
                0
              )
              : null
          },
          headers: { Authorization: `Bearer ${state.token}` },
        })

        return res?.response && res?.response.compliance
          ? res.response.id
          : false
      }))

      if (!saveID.includes(false)) {
        next({
          pathname, navigate, state,
          data: { saveID }
        })
      } else {
        setErrorMessage(true)
      }
    } catch (error) {
      setErrorMessage(true)
      console.log(error)
    } finally {
      setIsSpinnerLoading(false);
    }
  })


  return (
    <>
      {isSpinnerLoading && <Spinner />}
      {errorMessage && <ResponseMessage className='response-message' label={t('common:form.genericError')} />}

      <Title className={mainStyle['page-title']} text={<>{t('freemium:payment.creditCardTitle')} 💳</>} />
      <p className={mainStyle.subtitle}>{t('freemium:payment.subtitle')}</p>

      <form className={mainStyle.form} onSubmit={onSubmit}>

        <Input
          error={errors.owner}
          register={register('owner', { required })}
          label={t('common:paymentMethod.creditCard.owner.label')+'*'}
          placeholder={t('common:paymentMethod.creditCard.owner.placeholder')}
          disallowNumbers={true}
        />
        <InputError error={errors.owner} />


        <div className="input-label">{t('common:paymentMethod.creditCard.number.label')+'*'}</div>
        <Controller
          control={control}
          name="number"
          rules={numberValidationCardNumberFx(t)}
          render={({ field: { onBlur, onChange, value } }) => (
            <MaskedInput
              className={classNames('masked-input', errors.number && 'error')}
              mask={[/\d/, /\d/, /\d/, /\d/,
                ' ', /\d/, /\d/, /\d/, /\d/,
                ' ', /\d/, /\d/, /\d/, /\d/,
                ' ', /\d/, /\d/, /\d/, /\d/,
              ]}
              guide={false}
              keepCharPositions={false}
              onBlur={onBlur}
              onChange={value => onChange(value)}
              placeholder={t('common:paymentMethod.creditCard.number.placeholder')}
              value={value}
              disallowletters={value}
            />
          )}
        />
        <InputError error={errors.number} />


        <div className="input-label">{t('common:form.expirationDate.label')+'*'}</div>
        <Controller
          control={control}
          name="expirationDate"
          rules={expirationDateValidationFx(t)}
          render={({ field: { onBlur, onChange, value } }) => (
            <MaskedInput
              className={classNames('masked-input', errors.expirationDate && 'error')}
              mask={[/\d/, /\d/, '/', /\d/, /\d/]}
              guide={false}
              keepCharPositions={false}
              onBlur={onBlur}
              onChange={value => onChange(value)}
              placeholder={t('common:form.expirationDate.label')}
              value={value}
              disallowletters={value}
            />
          )}
        />
        <InputError error={errors.expirationDate} />


        <Input
          maxLength="4"
          error={errors.ccv}
          register={register('ccv', numberValidationCVCFx(t))}
          label={t('common:paymentMethod.creditCard.ccv.placeholderMobile')+'*'}
          placeholder={t('common:paymentMethod.creditCard.ccv.placeholderMobile')}
          disallowLetters={true}
        />
        <InputError error={errors.ccv} />


        <p className={mainStyle.required}>{t('common:form.mandatoryFields')}</p>


        <Button
          className={mainStyle.button}
          label={t('common:submit')}
          onClick={handleSubmit}
          type="primary"
          disabled={!isValid}
        />
      </form>
    </>
  )
}

export default CardForm